import React from 'react'
import {
  DcButton,
  DcDropDown,
  DcInput,
  PixelFactoryContext,
  PixelPageContainer,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'
import { addCategory } from 'utils/api-routes-accounts'

interface AddNewAccountProps {
  selectedID: {
    id: number | null
    title: string
  }
  handleClose: (_response: any, _action: string) => void
  treeNodes: any[]
}

const booleanOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const AddNewAccount: React.FC<AddNewAccountProps> = ({
  selectedID,
  handleClose,
  treeNodes,
}) => {
  const { showSuccess, showFailure } = React.useContext(
    PixelFactoryContext,
  ) as any
  const parentAccounts = React.useMemo(() => {
    const items = []
    const walk = (nodes) => {
      nodes.forEach((node) => {
        items.push(node)
        if (node.children?.length) {
          walk(node.children)
        }
      })
    }
    walk(treeNodes || [])
    return items
  }, [treeNodes])

  const [data, setData] = React.useState({
    code: '',
    name: '',
    description: '',
    account_type: 'asset_current',
    classification: 'asset',
    parent_account_id: selectedID.id || '',
    allow_posting: 'true',
    reconcile: 'false',
    is_active: 'true',
    is_control_account: 'false',
    default_partner_required: 'false',
    default_cost_center_required: 'false',
    currency_code: 'PKR',
    sort_order: '0',
  })

  const addNewAccount = () => {
    addCategory({
      ...data,
      parent_account_id: data.parent_account_id
        ? Number(data.parent_account_id)
        : null,
      allow_posting: data.allow_posting === 'true',
      reconcile: data.reconcile === 'true',
      is_active: data.is_active === 'true',
      is_control_account: data.is_control_account === 'true',
      default_partner_required: data.default_partner_required === 'true',
      default_cost_center_required:
        data.default_cost_center_required === 'true',
      sort_order: Number(data.sort_order || 0),
    }).subscribe({
      next: (res) => {
        showSuccess('Successfully added account')
        handleClose(res?.response, 'add')
      },
      error: () => showFailure('Failed to add account'),
    })
  }

  return (
    <PixelPageContainer>
      <SectionContent>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            addNewAccount()
          }}
        >
          <DcDropDown
            value={data.parent_account_id || ''}
            placeholder="Select Parent Account"
            options={parentAccounts.map((acc) => ({
              value: acc.id.toString(),
              label: acc.name,
            }))}
            onChange={(e) =>
              setData({ ...data, parent_account_id: e.target.value })
            }
          />
          <DcInput
            value={data.code}
            placeholder="Account Code"
            onChange={(e) => setData({ ...data, code: e.target.value })}
          />
          <DcInput
            value={data.name}
            placeholder="Account Name"
            onChange={(e) => setData({ ...data, name: e.target.value })}
          />
          <DcInput
            as="textarea"
            value={data.description}
            placeholder="Description"
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
          <DcDropDown
            value={data.classification}
            options={[
              { value: 'asset', label: 'Asset' },
              { value: 'liability', label: 'Liability' },
              { value: 'equity', label: 'Equity' },
              { value: 'income', label: 'Income' },
              { value: 'expense', label: 'Expense' },
            ]}
            onChange={(e) =>
              setData({ ...data, classification: e.target.value })
            }
          />
          <DcDropDown
            value={data.account_type}
            options={[
              { value: 'asset_cash', label: 'Bank and Cash' },
              { value: 'asset_current', label: 'Current Asset' },
              { value: 'asset_receivable', label: 'Receivable' },
              { value: 'liability_payable', label: 'Payable' },
              { value: 'liability_current', label: 'Current Liability' },
              { value: 'equity', label: 'Equity' },
              { value: 'income', label: 'Income' },
              { value: 'expense', label: 'Expense' },
              { value: 'expense_direct_cost', label: 'Direct Cost' },
            ]}
            onChange={(e) => setData({ ...data, account_type: e.target.value })}
          />
          <DcInput
            value={data.currency_code}
            placeholder="Currency Code"
            onChange={(e) =>
              setData({ ...data, currency_code: e.target.value })
            }
          />
          <DcInput
            type="number"
            value={data.sort_order}
            placeholder="Sort Order"
            onChange={(e) => setData({ ...data, sort_order: e.target.value })}
          />
          <DcDropDown
            value={data.allow_posting}
            options={booleanOptions}
            onChange={(e) =>
              setData({ ...data, allow_posting: e.target.value })
            }
          />
          <DcDropDown
            value={data.reconcile}
            options={booleanOptions}
            onChange={(e) => setData({ ...data, reconcile: e.target.value })}
          />
          <DcDropDown
            value={data.is_active}
            options={booleanOptions}
            onChange={(e) => setData({ ...data, is_active: e.target.value })}
          />
          <DcDropDown
            value={data.is_control_account}
            options={booleanOptions}
            onChange={(e) =>
              setData({ ...data, is_control_account: e.target.value })
            }
          />
          <DcDropDown
            value={data.default_partner_required}
            options={booleanOptions}
            onChange={(e) =>
              setData({ ...data, default_partner_required: e.target.value })
            }
          />
          <DcDropDown
            value={data.default_cost_center_required}
            options={booleanOptions}
            onChange={(e) =>
              setData({ ...data, default_cost_center_required: e.target.value })
            }
          />
          <DcButton variant="primary" type="submit">
            Save
          </DcButton>
        </Form>
      </SectionContent>
    </PixelPageContainer>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default AddNewAccount
